import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { 
  Plus, 
  Search, 
  FolderOpen, 
  Calendar,
  Trash2,
  Eye,
  FileText
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Projects() {
  const { getAuthHeaders } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { headers: getAuthHeaders() });
      setProjects(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des projets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API}/projects/${deleteId}`, { headers: getAuthHeaders() });
      setProjects(prev => prev.filter(p => p.id !== deleteId));
      toast.success("Projet supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteId(null);
    }
  };

  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.client_name?.toLowerCase().includes(search.toLowerCase())
  );

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'CHF' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-CH', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="space-y-6 fade-in">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">Projets</h1>
            <p className="text-gray-500 mt-1">
              {projects.length} projet{projects.length > 1 ? "s" : ""} au total
            </p>
          </div>
          <Link to="/new-quote">
            <Button className="btn-primary gap-2" data-testid="new-project-btn">
              <Plus className="h-5 w-5" />
              Nouveau projet
            </Button>
          </Link>
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Rechercher un projet..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-12"
            data-testid="search-input"
          />
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardContent className="p-6">
                  <div className="h-6 w-3/4 bg-gray-200 rounded mb-4" />
                  <div className="h-4 w-1/2 bg-gray-200 rounded mb-2" />
                  <div className="h-4 w-1/3 bg-gray-200 rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredProjects.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FolderOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {search ? "Aucun résultat" : "Aucun projet"}
              </h3>
              <p className="text-gray-500 mb-6">
                {search 
                  ? "Essayez une autre recherche" 
                  : "Créez votre premier projet de devis"}
              </p>
              {!search && (
                <Link to="/new-quote">
                  <Button className="btn-primary">
                    <Plus className="h-4 w-4 mr-2" />
                    Créer un projet
                  </Button>
                </Link>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project, idx) => (
              <Card 
                key={project.id} 
                className="card-hover group"
                style={{ animationDelay: `${idx * 0.05}s` }}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="h-10 w-10 rounded-lg bg-green-50 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-[#1E8B5A]" />
                    </div>
                    <div className={`
                      px-2 py-1 rounded-full text-xs font-medium
                      ${project.status === 'completed' ? 'bg-green-100 text-green-700' : 
                        project.status === 'in_progress' ? 'bg-blue-100 text-blue-700' : 
                        'bg-gray-100 text-gray-600'}
                    `}>
                      {project.status === 'completed' ? 'Terminé' : 
                       project.status === 'in_progress' ? 'En cours' : 'Brouillon'}
                    </div>
                  </div>

                  <h3 className="font-semibold text-gray-900 mb-1 truncate" data-testid={`project-name-${idx}`}>
                    {project.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-4">
                    {project.client_name || "Sans client"}
                  </p>

                  <div className="flex items-center justify-between text-sm mb-4">
                    <div className="flex items-center gap-1 text-gray-500">
                      <Calendar className="h-4 w-4" />
                      {formatDate(project.created_at)}
                    </div>
                    <span className="font-semibold text-[#1E8B5A]">
                      {formatCurrency(project.total_amount)}
                    </span>
                  </div>

                  <div className="text-xs text-gray-400 mb-4">
                    {project.items_count} élément{project.items_count > 1 ? "s" : ""} chiffré{project.items_count > 1 ? "s" : ""}
                  </div>

                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Link to={`/projects/${project.id}`} className="flex-1">
                      <Button variant="outline" size="sm" className="w-full gap-1">
                        <Eye className="h-4 w-4" />
                        Voir
                      </Button>
                    </Link>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setDeleteId(project.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-project-${idx}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce projet ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Tous les éléments chiffrés et l'historique 
                des conversations seront supprimés.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
